import { chunk } from 'lodash';
import React from 'react';
import { NavLink } from 'react-router-dom';
import { TABLET_PORTRAIT } from 'src/client/constants/mediaQueries';
import { useSelector } from 'src/client/redux';
import styled from 'styled-components/macro';
import { DescriptionText, PillButton, SectionTitle } from './styles';
import { Thumbnail } from './Thumbnail';
import { staticSplashContent } from './staticSplashContent';
import { ensure } from 'src/client/utils/type-utils';
import { StaticSplashThumbnail } from 'src/@types/StaticSplashResponse';

type Props = {
  thumbnails: StaticSplashThumbnail[];
  type: 'movies' | 'models' | 'galleries';
};

export const ThumbnailsSection = (props: Props) => {
  const { thumbnails = [], type } = props;

  const site = useSelector((state) => state.site);
  const [first = [], second = []] = chunk(thumbnails, thumbnails.length / 2);
  const GridComponent = gridComponents[type];
  const sectionText = ensure(staticSplashContent[site.abbreviation][type]);

  return (
    <Wrapper gridArea={props.type}>
      <GridComponent className="first" gridArea="first">
        {first.map((thumbnail) => (
          <Thumbnail key={thumbnail.UUID} thumbnail={thumbnail} type={type} />
        ))}
      </GridComponent>
      <InfoSection>
        <SectionTitle>{sectionText.title}</SectionTitle>
        <DescriptionText>{sectionText.description}</DescriptionText>
      </InfoSection>
      <GridComponent className="second" gridArea="second">
        {second.map((thumbnail) => (
          <Thumbnail key={thumbnail.UUID} thumbnail={thumbnail} type={type} />
        ))}
      </GridComponent>
      <ViewAllWrapper>
        <ViewAllButton as={NavLink} to={sectionText.path}>
          View All
        </ViewAllButton>
      </ViewAllWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.section<{ gridArea: string }>`
  display: grid;
  grid-area: ${(props) => props.gridArea};
  grid-auto-rows: min-content;
  grid-template-columns: 1fr;
  grid-row-gap: 5px;
  grid-template-areas:
    'first'
    'description'
    'second'
    'view-all';

  @media ${TABLET_PORTRAIT} {
    grid-template-columns: 1fr max-content;
    grid-template-areas:
      'description view-all'
      'first first'
      'second second';
  }
`;

const ModelsGrid = styled.div<{ gridArea: string }>`
  display: grid;
  grid-area: ${(props) => props.gridArea};
  grid-auto-rows: min-content;
  grid-gap: 5px;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(4, 1fr);
  padding: 0 5px;

  @media ${TABLET_PORTRAIT} {
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(2, 1fr);

    &&.second {
      .isFeatured {
        grid-column: 3/5;
        grid-row: 1/3;
      }
    }
  }

  .thumbnail {
    aspect-ratio: 173/260;

    &.isFeatured {
      grid-column: span 2;
      grid-row: span 2;
    }
  }
`;

const MoviesGrid = styled.div<{ gridArea: string }>`
  display: grid;
  grid-area: ${(props) => props.gridArea};
  grid-auto-rows: min-content;
  grid-gap: 5px;
  grid-template-columns: 1fr;
  padding: 0 5px;

  .thumbnail {
    aspect-ratio: 350 / 197;
  }

  @media ${TABLET_PORTRAIT} {
    grid-template-columns: repeat(3, 1fr);

    .thumbnail {
      &.isFeatured {
        grid-column: span 2;
        grid-row: span 2;
      }
    }

    &.second {
      .isFeatured {
        grid-column: 2/4;
        grid-row: 1/3;
      }
    }
  }
`;

const GalleriesGrid = styled.div<{ gridArea: string }>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: min-content;
  grid-gap: 5px;
  padding: 0 5px;
  grid-area: ${(props) => props.gridArea};

  @media ${TABLET_PORTRAIT} {
    grid-template-columns: repeat(9, 1fr);

    &.second {
      .isFeatured {
        grid-column: 3/5;
        grid-row: 1/3;

        @media ${TABLET_PORTRAIT} {
          grid-column: 1/6;
          grid-row: 1/2;
        }
      }
    }
  }

  .thumbnail {
    aspect-ratio: 173 / 260;

    @media ${TABLET_PORTRAIT} {
      grid-column: span 2;
    }

    &.isFeatured {
      aspect-ratio: 350 / 197;
      grid-column: span 2;

      @media ${TABLET_PORTRAIT} {
        aspect-ratio: auto;
        grid-column: span 5;
      }
    }
  }
`;

const InfoSection = styled.div`
  grid-area: description;
  padding: 10px 5px;

  @media ${TABLET_PORTRAIT} {
    padding: 15px 10px;
  }
`;

const ViewAllWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  grid-area: view-all;

  @media ${TABLET_PORTRAIT} {
    padding: 15px 10px;
  }
`;

const ViewAllButton = styled(PillButton)`
  background-color: black;
  border: 1px solid #666666;
  color: white;

  @media ${TABLET_PORTRAIT} {
    width: 120px;
  }
`;

const gridComponents = {
  models: ModelsGrid,
  movies: MoviesGrid,
  galleries: GalleriesGrid,
};
